import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLoadScript } from "@react-google-maps/api";
import { MdPerson, MdEmail, MdPhone, MdLocationOn, MdDoorBack, MdMarkunreadMailbox, MdMap, MdPublic } from "react-icons/md";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { verifyEmail, verifyPhone } from "../api/verification"; // Assuming these are utility functions for validation
import CustomSpinner from "../components/CustomSpinner"; // Assuming this is a spinner component    s
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { MdLock } from "react-icons/md";
import "../pages/Donation/StepPages/PaymentDetails/index.scss";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "../store/slices/donation";
import { validatePaymentForm } from "../utils/paymentUtils";
import mastercard from "../assets/images/mastercard.png";
import visa from "../assets/images/visa.png";

function InputField({
    type = "text",
    id,
    name,
    placeholder,
    value,
    onChange,
    onBlur,
    required = false,
    maxLength,
    icon,
    error,
    inputRef,
    loading,
    additionalErrorMessage
}) {
    return (
        <div className="input-with-icon-outer">
            <div className="input-with-icon">
                {icon && React.cloneElement(icon, { className: "input-icon" })}
                <input
                    type={type}
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    required={required}
                    maxLength={maxLength}
                    ref={inputRef}
                />
            </div>
            {error && <span className="error-text">{error}</span>}
            {loading ? (
                <CustomSpinner />
            ) : (
                additionalErrorMessage && (
                    <span className="error-text">{additionalErrorMessage}</span>
                )
            )}
        </div>
    );
}

function InputSelect({ icon, error, optionsComponent }) {
    return (
        <div className="input-with-icon-outer">
            <div className="input-with-icon">
                {icon && React.cloneElement(icon, { className: "input-icon" })}
                {optionsComponent}
            </div>
            {error && <span className="error-text">{error}</span>}
        </div>
    );
}
// The detail form for personal & address fields
export function PaymentFormDetails({ formErrorParent, onValidationChange }) {
    const { t } = useTranslation();

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: window?._ENV_?.REACT_APP_GOOGLE_MAPS_API_KEY ?? window._ENV_.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ["places"]
    });
    const addressInputRef = useRef(null);
    const initialFormValues = useSelector((state) => state.donation.userInfo);
    const [formValues, setFormValues] = useState(initialFormValues);
    const [formErrors, setFormErrors] = useState(formErrorParent);
    const [isCardComplete, setIsCardComplete] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {setFormErrors(formErrorParent)}, [formErrorParent]);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState(null);
    const [phoneLoading, setPhoneLoading] = useState(false);
    const [phoneErrorMessage, setPhoneErrorMessage] = useState(null);

    useEffect(() => {
     const { isValid }  = validatePaymentForm(formValues, t);
     if(isValid) {
        dispatch(setUserInfo(formValues));
     }
    }, [setUserInfo, formValues, dispatch]);
    
    // Notify parent of validation state changes
    useEffect(() => {
        const { isValid, errors } = validatePaymentForm(formValues, t);
        const isFormValid = isValid && isCardComplete && !emailErrorMessage;
        if (onValidationChange) {
            onValidationChange(isFormValid);
        }
    }, [formValues, isCardComplete, emailErrorMessage, t, onValidationChange]);
    
    useEffect(() => {
        if (isLoaded && addressInputRef.current) {
            const autocomplete = new window.google.maps.places.Autocomplete(
                addressInputRef.current,
                { types: ["address"] }
            );

            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                const addressComponents = place.address_components;
                let streetNumber = "";
                let route = "";
                let postal = "";
                let unit = "";
                let region = "";
                let country = "";

                addressComponents.forEach((component) => {
                    const types = component.types;
                    if (types.includes("street_number"))
                        streetNumber = component.long_name;
                    if (types.includes("route")) route = component.long_name;
                    if (types.includes("postal_code")) postal = component.long_name;
                    if (types.includes("subpremise")) unit = component.long_name;
                    if (types.includes("administrative_area_level_1"))
                        region = component.long_name;
                    if (types.includes("country")) country = component.short_name;
                });

                setFormValues((prev) => ({
                    ...prev,
                    addressLine1: `${streetNumber} ${route}`.trim(),
                    postalCode: postal,
                    unitNumber: unit,
                    selectedRegion: region,
                    selectedCountry: country
                }));
            });
        }
    }, [isLoaded, setFormValues]);

    function handleInputChange(field) {
        return (e) => {
            const { value } = e.target;
            setFormValues((prev) => ({ ...prev, [field]: value }));
            setFormErrors((prev) => ({ ...prev, [field]: "" }));
            // Clear email error message when user changes email
            if (field === "email") {
                setEmailErrorMessage(null);
            }
        };
    }

    function handlePhoneChange(e) {
        const numeric = e.target.value.replace(/\D/g, "");
        setFormValues((prev) => ({ ...prev, phone: numeric }));
        setFormErrors((prev) => ({ ...prev, phone: "" }));
    }

    function handleUnitNumberChange(e) {
        const numeric = e.target.value.replace(/\D/g, "");
        setFormValues((prev) => ({ ...prev, unitNumber: numeric }));
    }

    async function handleEmailBlur() {
        // Basic email regex validation first
        if (formValues.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
            setEmailErrorMessage(t("paymentDetailsInvalidEmail"));
            return;
        }
        

    }
    
    function handleCardChange(event) {
        setIsCardComplete(event.complete);
    }

    async function handlePhoneBlur() {
        setPhoneLoading(true);
        try {
            const resp = await verifyPhone(
                formValues.phone,
                formValues.selectedCountry
            );
            setPhoneErrorMessage(resp.isValid ? null : resp.message);
        } catch (err) {
            console.error("Error validating phone:", err.message);
        } finally {
            setPhoneLoading(false);
        }
    }

    const cardElementOptions = {
        hidePostalCode: true,
        disableLink: true,
        style: {
            base: {
                color: "#32325d",
                fontSize: "16px",
                fontSmoothing: "antialiased",
                backgroundColor: "#fff",
                "::placeholder": {
                    color: "#aab7c4"
                }
            },
            invalid: {
                color: "#fa755a",
                iconColor: "#fa755a"
            }
        }
    };


    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <form className="credit-card-info" style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <div className="details-form-row" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <span className="text-info" style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", width: "100%" }}>
                        {t("paymentDetailsSecureSSL")}
                        <MdLock className="lock-icon" />
                    </span>
                </div>
                <div className="details-form-row" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <div className="card-element-wrapper" style={{ width: "100%" }}>
                        <CardElement options={cardElementOptions} onChange={handleCardChange} />
                    </div>
                </div>
                <div className="details-form-row" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <InputField
                        id="firstName"
                        name="firstName"
                        placeholder={`${t("paymentDetailsFirstName")} *`}
                        value={formValues.firstName}
                        onChange={handleInputChange("firstName")}
                        required
                        icon={<MdPerson />}
                        error={formErrors.firstName}
                        maxLength="19"
                    />
                    <InputField
                        id="lastName"
                        name="lastName"
                        placeholder={`${t("paymentDetailsLastName")} *`}
                        value={formValues.lastName}
                        onChange={handleInputChange("lastName")}
                        required
                        icon={<MdPerson />}
                        error={formErrors.lastName}
                    />
                </div>

                <div className="details-form-row" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <InputField
                        type="email"
                        id="email"
                        name="email"
                        placeholder={`${t("paymentDetailsEmail")} *`}
                        value={formValues.email}
                        onChange={handleInputChange("email")}
                        onBlur={handleEmailBlur}
                        required
                        icon={<MdEmail />}
                        error={formErrors.email}
                        loading={emailLoading}
                        additionalErrorMessage={emailErrorMessage}
                    />

                    <InputField
                        id="phone"
                        name="phone"
                        placeholder={`${t("paymentDetailsPhone")} *`}
                        value={formValues.phone}
                        onChange={handlePhoneChange}
                        // onBlur={handlePhoneBlur}
                        required
                        icon={<MdPhone />}
                        error={formErrors.phone}
                        loading={phoneLoading}
                        additionalErrorMessage={phoneErrorMessage}
                    />
                </div>

                <div className="details-form-row" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <InputField
                        id="addressLine1"
                        name="addressLine1"
                        placeholder={`${t("paymentDetailsAddressLine1")} *`}
                        value={formValues.addressLine1}
                        onChange={handleInputChange("addressLine1")}
                        required
                        icon={<MdLocationOn />}
                        error={formErrors.addressLine1}
                        inputRef={addressInputRef}
                    />
                </div>
                <div className="details-form-row" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <InputField
                        id="unit"
                        name="unit"
                        placeholder={t("paymentDetailsUnitNumber")}
                        value={formValues.unitNumber}
                        onChange={handleUnitNumberChange}
                        icon={<MdDoorBack />}
                        error={formErrors.unitNumber}
                    />
                    <InputField
                        id="postal"
                        name="postal"
                        placeholder={`${t("paymentDetailsPostalCode")} *`}
                        value={formValues.postalCode}
                        onChange={handleInputChange("postalCode")}
                        required
                        icon={<MdMarkunreadMailbox />}
                        error={formErrors.postalCode}
                    />
                </div>

                <div className="details-form-row" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <InputSelect
                        id="selectedRegion"
                        name="selectedRegion"
                        placeholder={`${t("paymentDetailsSelectRegion")} *`}
                        value={formValues.selectedRegion}
                        onChange={(val) =>
                            handleInputChange("selectedRegion")({ target: { value: val } })
                        }
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '8px'
                        }}
                        required
                        icon={<MdMap />}
                        error={formErrors.selectedRegion}
                        optionsComponent={
                            <RegionDropdown
                                country={formValues.selectedCountry}
                                value={formValues.selectedRegion}
                                onChange={(val) =>
                                    handleInputChange("selectedRegion")({
                                        target: { value: val }
                                    })
                                }
                                countryValueType="short"
                                classes="region-dropdown"
                            />
                        }
                    />

                    <InputSelect
                        id="selectedCountry"
                        name="selectedCountry"
                        placeholder={`${t("paymentDetailsSelectCountry")} *`}
                        value={formValues.selectedCountry}
                        onChange={(val) =>
                            handleInputChange("selectedCountry")({ target: { value: val } })
                        }
                        required
                        icon={<MdPublic />}
                        error={formErrors.selectedCountry}
                        optionsComponent={
                            <CountryDropdown
                                value={formValues.selectedCountry}
                                onChange={(val) =>
                                    handleInputChange("selectedCountry")({
                                        target: { value: val }
                                    })
                                }
                                valueType="short"
                                classes="country-dropdown"
                            />
                        }
                    />
                </div>
                <span style={{ display: 'flex', justifyContent: 'center', width: "100%", marginTop: 16 }}>
                    <img 
                        src={visa}
                        alt="Visa" 
                        style={{ height: '20px', marginRight: '10px' }} 
                    />
                    <img 
                        src={mastercard}
                        alt="Mastercard" 
                        style={{ height: '20px' }} 
                    />
                </span>
            </form>
        </div>
    );
}
